import { useMemo } from 'react';

import { css } from '@emotion/react';
import { responsive, useTheme } from '@wci-tech/core-ui';

import { useThemeMode } from '@/context';
import { getContainerStyles } from '@/utils';
import { STICKY_HEADER_SHADOW } from '@/components/constants';

export const useStyles = (scrolled: boolean) => {
  const { theme } = useTheme();
  const { isDark } = useThemeMode();
  const linkActiveTextColor = isDark ? '#333333' : '#f5f6fa';

  return useMemo(
    () => ({
      navbar: css`
        width: 100%;
        background-color: var(--common-color);
        z-index: ${theme.zIndex.core.above + 4};
        position: relative;
        top: 0;

        &.scrolled {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          width: 100%;
          background-color: var(--common-color);
          box-shadow: ${STICKY_HEADER_SHADOW};
        }

        &.scrolled.animate-sticky {
          animation: slideDown 0.4s ease forwards;
        }

        @keyframes slideDown {
          from {
            transform: translateY(-100%);
          }

          to {
            transform: translateY(0);
          }
        }

        .calendly-overlay .calendly-popup-close {
          top: 25px !important;
        }
      `,

      socialMediaContainer: css`
        ${responsive.create({
          gap: {
            default: '12px',
            md: '10px',
            lg: '12px',
          },
        })}
      `,

      headerBookADemo: css`
        width: 100%;
        --btn-text-hover-color: #f5f6fa;
        --btn-text-active-color: #f5f6fa;
      `,

      navWrapper: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        position: relative;
      `,

      sideLogo: css`
        display: none;
        opacity: 0;
        margin-right: 40px;
        transition: opacity 0.3s ease;

        .scrolled & {
          display: block;
          opacity: 1;
        }
      `,

      toggleWrapper: css`
        --thumb-size: 20px;
        --thumb-offset-start: 6px;
        --thumb-offset-end: 6px;
      `,

      track: css`
        --ts-bg-color: #bdbdbd;
        --ts-active-color: #343434;
      `,

      rightControls: css`
        display: ${scrolled ? 'flex' : 'none'};
        align-items: center;
        gap: 16px;
        margin-left: 40px;
      `,

      stickyHamburger: css`
        display: none;

        @media (max-width: 1280px) {
          display: ${scrolled ? 'block' : 'none'};
        }
      `,

      navLinks: css`
        width: 100%;
        transition: all 0.3s ease;

        @media (max-width: ${scrolled ? '1240px' : '1058px'}) {
          display: none;
        }
      `,

      list: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 24px;
        list-style: none;
        margin: 0;
        padding: 0;
      `,

      container: css`
        ${getContainerStyles(theme)};

        ${responsive.create({
          padding: {
            default: `${theme.spacing.get(scrolled ? 8 : 16)} ${theme.spacing.get(
              36,
            )} ${theme.spacing.get(scrolled ? 8 : 32)} ${theme.spacing.get(36)}`,
          },
        })}
      `,

      drawerLogo: css`
        margin: 0 auto;
        padding-top: 60px;
      `,

      drawerLinkWrapper: (isActive: boolean) => css`
        cursor: pointer;
        width: 100%;
        transition:
          background-color 0.2s ease,
          color 0.2s ease;
        background-color: ${isActive ? 'var(--brand-color)' : 'transparent'};
        color: ${isActive ? linkActiveTextColor : 'var(--brand-color)'};

        a,
        span,
        svg {
          color: inherit;
        }

        svg {
          fill: currentColor;
        }

        &:hover {
          background-color: var(--brand-color);
          color: ${linkActiveTextColor};
        }
      `,

      drawerLinkText: (isActive: boolean) => css`
        font-size: 16px;
        color: inherit;
      `,

      socialMediaWrapper: css`
        gap: 20px;
      `,

      socialIcon: css`
        color: var(--text-color);
      `,

      hamburgerButton: css`
        background-color: transparent;
        border: none;

        ${responsive.create({
          padding: {
            default: 0,
            md: 0,
          },
        })}

        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        &&:hover {
          background-color: transparent;
        }
      `,

      drawer: css`
        background-color: var(--common-color);
      `,

      actionButton: css`
        background: linear-gradient(234.66deg, #4054d1 -1.18%, #17ceca 112.11%);
        white-space: nowrap;
      `,
    }),
    [theme, scrolled, isDark, linkActiveTextColor],
  );
};
